import { useState, useMemo, useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart,
  Cell,
  PieChart,
  Pie,
} from "recharts";

// ─── RAW DATA ────────────────────────────────────────────────────────────────
const RAW_POLLS = [
  {
    poll: "MDW Communications",
    date: "2025-06-15",
    candidates: {
      "Kat Abughazaleh": 10,
      "Bushra Amiwala": 2,
      "Daniel Biss": 17,
      "Laura Fine": 8,
      "Hoan Huynh": 0,
      "Mike Simmons": 0,
      "Phil Andrew": 0,
      Undecided: 53,
    },
  },
  {
    poll: "MDW Communications",
    date: "2025-10-16",
    candidates: {
      "Kat Abughazaleh": 13,
      "Bushra Amiwala": 2,
      "Daniel Biss": 18,
      "Laura Fine": 9,
      "Hoan Huynh": 0,
      "Mike Simmons": 0,
      "Phil Andrew": 0,
      Undecided: 48,
    },
  },
  {
    poll: "Data for Progress",
    date: "2025-11-03",
    candidates: {
      "Kat Abughazaleh": 18,
      "Bushra Amiwala": 6,
      "Daniel Biss": 18,
      "Laura Fine": 10,
      "Hoan Huynh": 0,
      "Mike Simmons": 6,
      "Phil Andrew": 0,
      Undecided: 31,
    },
  },
  {
    poll: "Impact Research",
    date: "2025-11-09",
    candidates: {
      "Kat Abughazaleh": 17,
      "Bushra Amiwala": 3,
      "Daniel Biss": 31,
      "Laura Fine": 10,
      "Hoan Huynh": 4,
      "Mike Simmons": 6,
      "Phil Andrew": 3,
      Undecided: 21,
    },
  },
  {
    poll: "GBAO Strategies",
    date: "2026-01-29",
    candidates: {
      "Kat Abughazaleh": 14,
      "Bushra Amiwala": 4,
      "Daniel Biss": 21,
      "Laura Fine": 21,
      "Hoan Huynh": 2,
      "Mike Simmons": 7,
      "Phil Andrew": 4,
      Undecided: 23,
    },
  },
  {
    poll: "Impact Research",
    date: "2026-02-10",
    candidates: {
      "Kat Abughazaleh": 18,
      "Bushra Amiwala": 4,
      "Daniel Biss": 31,
      "Laura Fine": 18,
      "Hoan Huynh": 3,
      "Mike Simmons": 7,
      "Phil Andrew": 7,
      Undecided: 11,
    },
  },
  {
    poll: "Public Policy Polling",
    date: "2026-03-10",
    candidates: {
      "Kat Abughazaleh": 20,
      "Bushra Amiwala": 6,
      "Daniel Biss": 24,
      "Laura Fine": 14,
      "Hoan Huynh": 1,
      "Mike Simmons": 10,
      "Phil Andrew": 7,
      Undecided: 17,
    },
  },
];

const GRADE_WEIGHTS = {
  "A+": 1.0,
  A: 0.9,
  "A-": 0.8,
  "B+": 0.7,
  B: 0.6,
  "B-": 0.5,
  "B/C": 0.4,
  "C+": 0.3,
  C: 0.25,
};

const POLLSTER_GRADES = {
  "Impact Research": "B+",
  "GBAO Strategies": "B/C",
  "Data for Progress": "B+",
  "MDW Communications": "B/C",
  "Public Policy Polling": "B",
};

const DEFAULT_POLL_GRADE = "C";

const POLLS = RAW_POLLS.map((poll) => {
  const grade = POLLSTER_GRADES[poll.poll] || DEFAULT_POLL_GRADE;
  const weight = GRADE_WEIGHTS[grade] || GRADE_WEIGHTS[DEFAULT_POLL_GRADE];
  return { ...poll, grade, weight };
});

const KALSHI_KAT = [
  { date: "2025-03-30", prob: 15 },
  { date: "2025-03-31", prob: 25 },
  { date: "2025-04-09", prob: 16 },
  { date: "2025-04-23", prob: 15 },
  { date: "2025-04-24", prob: 16 },
  { date: "2025-04-25", prob: 17 },
  { date: "2025-04-30", prob: 16 },
  { date: "2025-05-01", prob: 19 },
  { date: "2025-05-30", prob: 15 },
  { date: "2025-06-26", prob: 19 },
  { date: "2025-06-27", prob: 22 },
  { date: "2025-06-28", prob: 23 },
  { date: "2025-06-29", prob: 28 },
  { date: "2025-07-01", prob: 28 },
  { date: "2025-07-03", prob: 20 },
  { date: "2025-07-04", prob: 27 },
  { date: "2025-07-08", prob: 23 },
  { date: "2025-07-13", prob: 21 },
  { date: "2025-07-15", prob: 15 },
  { date: "2025-07-17", prob: 12 },
  { date: "2025-07-19", prob: 23 },
  { date: "2025-07-24", prob: 22 },
  { date: "2025-08-03", prob: 15 },
  { date: "2025-08-09", prob: 25 },
  { date: "2025-09-07", prob: 30 },
  { date: "2025-09-09", prob: 29 },
  { date: "2025-09-11", prob: 30 },
  { date: "2025-09-13", prob: 30 },
  { date: "2025-09-19", prob: 30 },
  { date: "2025-09-20", prob: 30 },
  { date: "2025-09-21", prob: 30 },
  { date: "2025-09-25", prob: 32 },
  { date: "2025-09-26", prob: 31 },
  { date: "2025-10-01", prob: 17 },
  { date: "2025-10-08", prob: 17 },
  { date: "2025-10-09", prob: 31 },
  { date: "2025-10-16", prob: 32 },
  { date: "2025-10-19", prob: 32 },
  { date: "2025-10-21", prob: 32 },
  { date: "2025-10-22", prob: 21 },
  { date: "2025-10-23", prob: 34 },
  { date: "2025-10-24", prob: 35 },
  { date: "2025-10-27", prob: 33 },
  { date: "2025-10-28", prob: 34 },
  { date: "2025-10-30", prob: 54 },
  { date: "2025-10-31", prob: 45 },
  { date: "2025-11-04", prob: 45 },
  { date: "2025-11-08", prob: 43 },
  { date: "2025-11-11", prob: 43 },
  { date: "2025-11-14", prob: 33 },
  { date: "2025-11-17", prob: 33 },
  { date: "2025-11-21", prob: 35 },
  { date: "2025-11-22", prob: 44 },
  { date: "2025-11-24", prob: 36 },
  { date: "2025-11-25", prob: 46 },
  { date: "2025-11-26", prob: 45 },
  { date: "2025-11-28", prob: 43 },
  { date: "2025-11-29", prob: 44 },
  { date: "2025-12-01", prob: 35 },
  { date: "2025-12-04", prob: 37 },
  { date: "2025-12-05", prob: 38 },
  { date: "2025-12-08", prob: 32 },
  { date: "2025-12-09", prob: 39 },
  { date: "2025-12-11", prob: 30 },
  { date: "2025-12-12", prob: 32 },
  { date: "2025-12-13", prob: 31 },
  { date: "2025-12-14", prob: 32 },
  { date: "2025-12-15", prob: 31 },
  { date: "2025-12-16", prob: 32 },
  { date: "2025-12-19", prob: 32 },
  { date: "2025-12-22", prob: 32 },
  { date: "2025-12-23", prob: 32 },
  { date: "2025-12-24", prob: 32 },
  { date: "2025-12-26", prob: 30 },
  { date: "2025-12-27", prob: 32 },
  { date: "2025-12-28", prob: 32 },
  { date: "2025-12-30", prob: 32 },
  { date: "2025-12-31", prob: 32 },
  { date: "2026-01-02", prob: 29 },
  { date: "2026-01-04", prob: 29 },
  { date: "2026-01-05", prob: 26 },
  { date: "2026-01-07", prob: 20 },
  { date: "2026-01-08", prob: 20 },
  { date: "2026-01-09", prob: 16 },
  { date: "2026-01-10", prob: 17 },
  { date: "2026-01-11", prob: 17 },
  { date: "2026-01-12", prob: 12 },
  { date: "2026-01-13", prob: 18 },
  { date: "2026-01-14", prob: 18 },
  { date: "2026-01-15", prob: 12 },
  { date: "2026-01-16", prob: 17 },
  { date: "2026-01-17", prob: 23 },
  { date: "2026-01-18", prob: 21 },
  { date: "2026-01-19", prob: 15 },
  { date: "2026-01-20", prob: 14 },
  { date: "2026-01-21", prob: 23 },
  { date: "2026-01-22", prob: 24 },
  { date: "2026-01-23", prob: 24 },
  { date: "2026-01-24", prob: 23 },
  { date: "2026-01-25", prob: 22 },
  { date: "2026-01-26", prob: 20 },
  { date: "2026-01-27", prob: 19 },
  { date: "2026-01-28", prob: 18 },
  { date: "2026-01-29", prob: 20 },
  { date: "2026-01-30", prob: 17 },
  { date: "2026-01-31", prob: 18 },
  { date: "2026-02-01", prob: 20 },
  { date: "2026-02-02", prob: 19 },
  { date: "2026-02-03", prob: 15 },
  { date: "2026-02-04", prob: 14 },
  { date: "2026-02-05", prob: 14 },
  { date: "2026-02-06", prob: 18 },
  { date: "2026-02-07", prob: 18 },
  { date: "2026-02-08", prob: 37 },
  { date: "2026-02-09", prob: 21 },
  { date: "2026-02-10", prob: 22 },
  { date: "2026-02-11", prob: 22 },
  { date: "2026-02-12", prob: 30 },
  { date: "2026-02-13", prob: 17 },
  { date: "2026-02-14", prob: 20 },
  { date: "2026-02-15", prob: 28 },
  { date: "2026-02-16", prob: 17 },
  { date: "2026-02-17", prob: 14 },
  { date: "2026-02-18", prob: 14 },
  { date: "2026-02-19", prob: 22 },
  { date: "2026-02-20", prob: 23 },
  { date: "2026-02-21", prob: 22 },
  { date: "2026-02-22", prob: 21 },
  { date: "2026-02-23", prob: 20 },
  { date: "2026-02-24", prob: 21 },
  { date: "2026-02-25", prob: 21 },
  { date: "2026-02-26", prob: 20 },
  { date: "2026-02-27", prob: 23 },
  { date: "2026-02-28", prob: 26 },
  { date: "2026-03-01", prob: 29 },
  { date: "2026-03-02", prob: 30 },
  { date: "2026-03-03", prob: 27 },
  { date: "2026-03-04", prob: 26 },
  { date: "2026-03-05", prob: 26 },
  { date: "2026-03-06", prob: 25 },
  { date: "2026-03-07", prob: 29 },
  { date: "2026-03-08", prob: 29 },
  { date: "2026-03-09", prob: 26 },
  { date: "2026-03-10", prob: 20 },
  { date: "2026-03-11", prob: 32 },
  { date: "2026-03-12", prob: 38 },
  { date: "2026-03-13", prob: 34 },
];
const KALSHI_BISS = [
  { date: "2025-04-24", prob: 58 },
  { date: "2025-05-16", prob: 75 },
  { date: "2025-05-24", prob: 67 },
  { date: "2025-07-22", prob: 72 },
  { date: "2025-07-23", prob: 73 },
  { date: "2025-08-03", prob: 68 },
  { date: "2025-08-07", prob: 70 },
  { date: "2025-09-11", prob: 68 },
  { date: "2025-09-19", prob: 68 },
  { date: "2025-09-27", prob: 66 },
  { date: "2025-10-01", prob: 65 },
  { date: "2025-10-22", prob: 65 },
  { date: "2025-10-28", prob: 65 },
  { date: "2025-10-31", prob: 60 },
  { date: "2025-11-04", prob: 64 },
  { date: "2025-11-05", prob: 50 },
  { date: "2025-11-06", prob: 64 },
  { date: "2025-11-08", prob: 66 },
  { date: "2025-11-11", prob: 65 },
  { date: "2025-11-17", prob: 63 },
  { date: "2025-11-21", prob: 61 },
  { date: "2025-11-24", prob: 58 },
  { date: "2025-11-26", prob: 57 },
  { date: "2025-12-01", prob: 50 },
  { date: "2025-12-03", prob: 41 },
  { date: "2025-12-04", prob: 50 },
  { date: "2025-12-06", prob: 58 },
  { date: "2025-12-08", prob: 58 },
  { date: "2025-12-09", prob: 51 },
  { date: "2025-12-11", prob: 58 },
  { date: "2025-12-15", prob: 58 },
  { date: "2025-12-22", prob: 44 },
  { date: "2025-12-23", prob: 45 },
  { date: "2025-12-30", prob: 46 },
  { date: "2026-01-01", prob: 43 },
  { date: "2026-01-02", prob: 43 },
  { date: "2026-01-05", prob: 42 },
  { date: "2026-01-06", prob: 60 },
  { date: "2026-01-07", prob: 73 },
  { date: "2026-01-08", prob: 73 },
  { date: "2026-01-09", prob: 61 },
  { date: "2026-01-10", prob: 65 },
  { date: "2026-01-11", prob: 75 },
  { date: "2026-01-12", prob: 74 },
  { date: "2026-01-13", prob: 74 },
  { date: "2026-01-14", prob: 71 },
  { date: "2026-01-15", prob: 76 },
  { date: "2026-01-16", prob: 79 },
  { date: "2026-01-17", prob: 80 },
  { date: "2026-01-18", prob: 75 },
  { date: "2026-01-19", prob: 73 },
  { date: "2026-01-20", prob: 74 },
  { date: "2026-01-21", prob: 74 },
  { date: "2026-01-22", prob: 75 },
  { date: "2026-01-23", prob: 70 },
  { date: "2026-01-24", prob: 63 },
  { date: "2026-01-25", prob: 68 },
  { date: "2026-01-26", prob: 72 },
  { date: "2026-01-27", prob: 72 },
  { date: "2026-01-28", prob: 61 },
  { date: "2026-01-29", prob: 65 },
  { date: "2026-01-30", prob: 68 },
  { date: "2026-01-31", prob: 72 },
  { date: "2026-02-01", prob: 69 },
  { date: "2026-02-02", prob: 69 },
  { date: "2026-02-03", prob: 68 },
  { date: "2026-02-04", prob: 65 },
  { date: "2026-02-05", prob: 62 },
  { date: "2026-02-06", prob: 61 },
  { date: "2026-02-07", prob: 60 },
  { date: "2026-02-08", prob: 59 },
  { date: "2026-02-09", prob: 62 },
  { date: "2026-02-10", prob: 56 },
  { date: "2026-02-11", prob: 55 },
  { date: "2026-02-12", prob: 62 },
  { date: "2026-02-13", prob: 71 },
  { date: "2026-02-14", prob: 64 },
  { date: "2026-02-15", prob: 69 },
  { date: "2026-02-16", prob: 68 },
  { date: "2026-02-17", prob: 72 },
  { date: "2026-02-18", prob: 66 },
  { date: "2026-02-19", prob: 63 },
  { date: "2026-02-20", prob: 64 },
  { date: "2026-02-21", prob: 61 },
  { date: "2026-02-22", prob: 63 },
  { date: "2026-02-23", prob: 61 },
  { date: "2026-02-24", prob: 58 },
  { date: "2026-02-25", prob: 59 },
  { date: "2026-02-26", prob: 67 },
  { date: "2026-02-27", prob: 62 },
  { date: "2026-02-28", prob: 62 },
  { date: "2026-03-01", prob: 63 },
  { date: "2026-03-02", prob: 63 },
  { date: "2026-03-03", prob: 65 },
  { date: "2026-03-04", prob: 66 },
  { date: "2026-03-05", prob: 69 },
  { date: "2026-03-06", prob: 68 },
  { date: "2026-03-07", prob: 66 },
  { date: "2026-03-08", prob: 69 },
  { date: "2026-03-09", prob: 72 },
  { date: "2026-03-10", prob: 75 },
  { date: "2026-03-11", prob: 69 },
  { date: "2026-03-12", prob: 62 },
  { date: "2026-03-13", prob: 65 },
];
const KALSHI_FINE = [
  { date: "2025-04-24", prob: 25 },
  { date: "2025-09-20", prob: 1 },
  { date: "2025-09-21", prob: 1 },
  { date: "2025-09-26", prob: 1 },
  { date: "2025-10-22", prob: 6 },
  { date: "2025-10-27", prob: 6 },
  { date: "2025-10-31", prob: 2 },
  { date: "2025-11-04", prob: 5 },
  { date: "2025-11-06", prob: 7 },
  { date: "2025-11-26", prob: 1 },
  { date: "2025-11-27", prob: 17 },
  { date: "2025-11-28", prob: 9 },
  { date: "2025-11-30", prob: 17 },
  { date: "2025-12-04", prob: 14 },
  { date: "2025-12-05", prob: 5 },
  { date: "2025-12-12", prob: 13 },
  { date: "2025-12-21", prob: 18 },
  { date: "2025-12-22", prob: 20 },
  { date: "2025-12-23", prob: 20 },
  { date: "2025-12-24", prob: 18 },
  { date: "2025-12-25", prob: 17 },
  { date: "2026-01-01", prob: 31 },
  { date: "2026-01-02", prob: 27 },
  { date: "2026-01-06", prob: 24 },
  { date: "2026-01-07", prob: 14 },
  { date: "2026-01-08", prob: 5 },
  { date: "2026-01-09", prob: 8 },
  { date: "2026-01-10", prob: 15 },
  { date: "2026-01-12", prob: 13 },
  { date: "2026-01-17", prob: 2 },
  { date: "2026-01-18", prob: 12 },
  { date: "2026-01-20", prob: 15 },
  { date: "2026-01-24", prob: 8 },
  { date: "2026-01-25", prob: 7 },
  { date: "2026-01-27", prob: 9 },
  { date: "2026-01-28", prob: 12 },
  { date: "2026-01-29", prob: 12 },
  { date: "2026-01-30", prob: 12 },
  { date: "2026-01-31", prob: 13 },
  { date: "2026-02-01", prob: 13 },
  { date: "2026-02-03", prob: 18 },
  { date: "2026-02-04", prob: 23 },
  { date: "2026-02-05", prob: 25 },
  { date: "2026-02-06", prob: 23 },
  { date: "2026-02-07", prob: 22 },
  { date: "2026-02-08", prob: 21 },
  { date: "2026-02-09", prob: 24 },
  { date: "2026-02-10", prob: 22 },
  { date: "2026-02-11", prob: 27 },
  { date: "2026-02-12", prob: 17 },
  { date: "2026-02-13", prob: 15 },
  { date: "2026-02-14", prob: 24 },
  { date: "2026-02-16", prob: 16 },
  { date: "2026-02-17", prob: 13 },
  { date: "2026-02-18", prob: 16 },
  { date: "2026-02-19", prob: 12 },
  { date: "2026-02-20", prob: 12 },
  { date: "2026-02-21", prob: 13 },
  { date: "2026-02-22", prob: 15 },
  { date: "2026-02-23", prob: 16 },
  { date: "2026-02-24", prob: 19 },
  { date: "2026-02-25", prob: 17 },
  { date: "2026-02-26", prob: 7 },
  { date: "2026-02-27", prob: 9 },
  { date: "2026-02-28", prob: 4 },
  { date: "2026-03-01", prob: 6 },
  { date: "2026-03-02", prob: 6 },
  { date: "2026-03-03", prob: 6 },
  { date: "2026-03-04", prob: 4 },
  { date: "2026-03-05", prob: 6 },
  { date: "2026-03-06", prob: 5 },
  { date: "2026-03-07", prob: 3 },
  { date: "2026-03-08", prob: 2 },
  { date: "2026-03-09", prob: 4 },
  { date: "2026-03-10", prob: 4 },
  { date: "2026-03-11", prob: 2 },
  { date: "2026-03-12", prob: 7 },
  { date: "2026-03-13", prob: 4 },
];

// ─── COLORS ──────────────────────────────────────────────────────────────────
const COLORS = {
  "Kat Abughazaleh": "#E8453C",
  "Daniel Biss": "#4A6FA5",
  "Laura Fine": "#7B9E6B",
  "Mike Simmons": "#C47F17",
  "Bushra Amiwala": "#9B6B9E",
  "Hoan Huynh": "#5DADE2",
  "Phil Andrew": "#8D8D8D",
  Undecided: "#C0C0C0",
  "Progressive Coalition": "#E8453C",
};

const PROGRESSIVE_CANDIDATES = [
  "Kat Abughazaleh",
  "Bushra Amiwala",
  "Mike Simmons",
  "Hoan Huynh",
];
const ALL_DECIDED = [
  "Kat Abughazaleh",
  "Bushra Amiwala",
  "Daniel Biss",
  "Laura Fine",
  "Hoan Huynh",
  "Mike Simmons",
  "Phil Andrew",
];

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

const SectionHeader = ({ number, title, subtitle }) => (
  <div style={{ marginBottom: 28, marginTop: 48 }}>
    <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
      <span
        style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 13,
          fontWeight: 600,
          color: "#E8453C",
          letterSpacing: 2,
          textTransform: "uppercase",
        }}
      >
        {number}
      </span>
      <h2
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 28,
          fontWeight: 700,
          color: "#1a1a1a",
          margin: 0,
          lineHeight: 1.2,
        }}
      >
        {title}
      </h2>
    </div>
    {subtitle && (
      <p
        style={{
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: 15,
          color: "#666",
          margin: "6px 0 0 0",
          paddingLeft: 42,
          maxWidth: 650,
        }}
      >
        {subtitle}
      </p>
    )}
  </div>
);

const StatCard = ({ value, label, sublabel, color = "#E8453C", large }) => (
  <div
    style={{
      background: "white",
      borderRadius: 10,
      padding: large ? "22px 26px" : "16px 20px",
      border: "1px solid #e8e8e8",
      flex: 1,
      minWidth: 140,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}
  >
    <div
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: large ? 36 : 28,
        fontWeight: 700,
        color,
        lineHeight: 1.1,
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontFamily: "'Source Sans 3', sans-serif",
        fontSize: 13,
        color: "#555",
        marginTop: 4,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {label}
    </div>
    {sublabel && (
      <div
        style={{
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: 12,
          color: "#999",
          marginTop: 2,
        }}
      >
        {sublabel}
      </div>
    )}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const source = payload[0]?.payload;
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.97)",
        border: "1px solid #ddd",
        borderRadius: 8,
        padding: "10px 14px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
        fontFamily: "'Source Sans 3', sans-serif",
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6, color: "#333" }}>
        {label}
      </div>
      {payload.map((p, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 3,
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: p.color,
            }}
          />
          <span style={{ color: "#666" }}>{p.name}:</span>
          <span style={{ fontWeight: 600, color: "#222" }}>
            {typeof p.value === "number" ? p.value.toFixed(1) + "%" : p.value}
          </span>
        </div>
      ))}
      {source?.pollGrade && (
        <div
          style={{
            marginTop: 6,
            paddingTop: 6,
            borderTop: "1px solid #eee",
            color: "#777",
            fontSize: 11,
          }}
        >
          Pollster grade: {source.pollGrade} · weight:{" "}
          {source.pollWeight.toFixed(2)}
        </div>
      )}
    </div>
  );
};

// ─── MAIN DASHBOARD ──────────────────────────────────────────────────────────
function IL09Dashboard() {
  const [activeSection, setActiveSection] = useState(0);
  const [scenarioDropouts, setScenarioDropouts] = useState(
    new Set(["Bushra Amiwala", "Mike Simmons", "Hoan Huynh"]),
  );
  const [redistributionTarget, setRedistributionTarget] =
    useState("Kat Abughazaleh");
  const [animatedPct, setAnimatedPct] = useState(0);
  const [now, setNow] = useState(() => new Date());
  const [kalshiWindow, setKalshiWindow] = useState("ALL");

  const latestPoll = POLLS[POLLS.length - 1];
  const primaryDate = useMemo(() => new Date("2026-03-17T00:00:00"), []);
  const msUntilPrimary = primaryDate.getTime() - now.getTime();
  const daysUntilPrimary = Math.max(0, Math.ceil(msUntilPrimary / 86400000));
  const untilPrimaryLabel =
    daysUntilPrimary === 0
      ? "Today"
      : `${daysUntilPrimary} day${daysUntilPrimary === 1 ? "" : "s"}`;
  const untilPrimaryBodyText =
    daysUntilPrimary === 0
      ? "With the primary happening today"
      : `With ${untilPrimaryLabel.toLowerCase()} until March 17`;

  // Animate the hero stat
  useEffect(() => {
    const target = 45;
    let current = 0;
    const interval = setInterval(() => {
      current += 1;
      if (current >= target) {
        setAnimatedPct(target);
        clearInterval(interval);
      } else setAnimatedPct(current);
    }, 30);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Build poll trend data
  const trendData = useMemo(() => {
    const weightedSums = Object.fromEntries(ALL_DECIDED.map((c) => [c, 0]));
    let progressiveWeightedSum = 0;
    let totalWeight = 0;

    return POLLS.map((p) => {
      const decided = {};
      let total = 0;
      ALL_DECIDED.forEach((c) => {
        decided[c] = p.candidates[c] || 0;
        total += p.candidates[c] || 0;
      });
      const row = {
        date: new Date(p.date).toLocaleDateString("en-US", {
          month: "short",
          year: "2-digit",
        }),
        rawDate: p.date,
        poll: p.poll,
        pollGrade: p.grade,
        pollWeight: p.weight,
      };
      ALL_DECIDED.forEach((c) => {
        const pct = (decided[c] / total) * 100;
        row[c] = +pct.toFixed(1);
        weightedSums[c] += pct * p.weight;
      });
      // progressive coalition
      let progTotal = 0;
      PROGRESSIVE_CANDIDATES.forEach((c) => (progTotal += decided[c]));
      const progressivePct = (progTotal / total) * 100;
      row["Progressive Coalition"] = +progressivePct.toFixed(1);

      totalWeight += p.weight;
      progressiveWeightedSum += progressivePct * p.weight;
      ALL_DECIDED.forEach((c) => {
        row[`${c} (Grade-Weighted Avg)`] = +(
          weightedSums[c] / totalWeight
        ).toFixed(1);
      });
      row["Progressive Coalition (Grade-Weighted Avg)"] = +(
        progressiveWeightedSum / totalWeight
      ).toFixed(1);
      return row;
    });
  }, []);

  // Kalshi combined timeline
  const kalshiData = useMemo(() => {
    const dateMap = {};
    KALSHI_BISS.forEach((d) => {
      dateMap[d.date] = {
        ...(dateMap[d.date] || {}),
        date: d.date,
        "Daniel Biss": d.prob,
      };
    });
    KALSHI_KAT.forEach((d) => {
      dateMap[d.date] = {
        ...(dateMap[d.date] || {}),
        date: d.date,
        "Kat Abughazaleh": d.prob,
      };
    });
    KALSHI_FINE.forEach((d) => {
      dateMap[d.date] = {
        ...(dateMap[d.date] || {}),
        date: d.date,
        "Laura Fine": d.prob,
      };
    });
    return Object.values(dateMap)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => ({
        ...d,
        label: new Date(d.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
      }));
  }, []);

  const filteredKalshiData = useMemo(() => {
    if (kalshiWindow === "ALL") return kalshiData;
    const months = kalshiWindow === "1M" ? 1 : 6;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - months);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    return kalshiData.filter((d) => d.date >= cutoffStr);
  }, [kalshiData, kalshiWindow]);

  // Scenario builder
  const scenarioData = useMemo(() => {
    const latest = { ...latestPoll.candidates };
    delete latest["Undecided"];
    const other = latestPoll.candidates["Undecided"] || 0;

    const totalDecided = ALL_DECIDED.reduce((s, c) => s + (latest[c] || 0), 0);
    const redistributed = {};
    let pooledVotes = 0;

    ALL_DECIDED.forEach((c) => {
      if (scenarioDropouts.has(c)) {
        pooledVotes += latest[c] || 0;
        redistributed[c] = 0;
      } else {
        redistributed[c] = latest[c] || 0;
      }
    });

    redistributed[redistributionTarget] =
      (redistributed[redistributionTarget] || 0) + pooledVotes;

    const newTotal = ALL_DECIDED.reduce((s, c) => s + redistributed[c], 0);

    return ALL_DECIDED.filter((c) => redistributed[c] > 0)
      .map((c) => ({
        name: c,
        original: +(((latest[c] || 0) / totalDecided) * 100).toFixed(1),
        scenario: +((redistributed[c] / newTotal) * 100).toFixed(1),
        color: COLORS[c],
        isTarget: c === redistributionTarget,
      }))
      .sort((a, b) => b.scenario - a.scenario);
  }, [scenarioDropouts, redistributionTarget]);

  // Progressive bloc comparison per poll
  const blocData = useMemo(() => {
    return POLLS.map((p) => {
      const all = { ...p.candidates };
      delete all["Undecided"];
      delete all["Other"];
      const total = Object.values(all).reduce((s, v) => s + v, 0);
      let prog = 0;
      PROGRESSIVE_CANDIDATES.forEach((c) => (prog += all[c] || 0));
      return {
        poll: p.poll.split("(")[0].trim(),
        pollGrade: p.grade,
        pollWeight: p.weight,
        date: new Date(p.date).toLocaleDateString("en-US", {
          month: "short",
          year: "2-digit",
        }),
        Biss: +(((all["Daniel Biss"] || 0) / total) * 100).toFixed(1),
        "Progressive Bloc": +((prog / total) * 100).toFixed(1),
        "Fine + Andrew": +(
          (((all["Laura Fine"] || 0) + (all["Phil Andrew"] || 0)) / total) *
          100
        ).toFixed(1),
      };
    });
  }, []);

  const weightedBlocSummary = useMemo(() => {
    const totals = { prog: 0, biss: 0, fineAndrew: 0, weights: 0 };
    POLLS.forEach((p) => {
      const all = { ...p.candidates };
      delete all["Undecided"];
      delete all["Other"];
      const total = Object.values(all).reduce((s, v) => s + v, 0);
      const progressive = PROGRESSIVE_CANDIDATES.reduce(
        (s, c) => s + (all[c] || 0),
        0,
      );
      const biss = all["Daniel Biss"] || 0;
      const fineAndrew = (all["Laura Fine"] || 0) + (all["Phil Andrew"] || 0);
      totals.weights += p.weight;
      totals.prog += (progressive / total) * 100 * p.weight;
      totals.biss += (biss / total) * 100 * p.weight;
      totals.fineAndrew += (fineAndrew / total) * 100 * p.weight;
    });

    return [
      {
        name: "Progressive Bloc",
        value: +(totals.prog / totals.weights).toFixed(1),
        color: "#E8453C",
      },
      {
        name: "Biss",
        value: +(totals.biss / totals.weights).toFixed(1),
        color: "#4A6FA5",
      },
      {
        name: "Fine + Andrew",
        value: +(totals.fineAndrew / totals.weights).toFixed(1),
        color: "#7B9E6B",
      },
    ];
  }, []);

  const toggleDropout = (c) => {
    setScenarioDropouts((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      if (next.has(redistributionTarget)) {
        const remaining = ALL_DECIDED.filter((x) => !next.has(x));
        if (remaining.length > 0) setRedistributionTarget(remaining[0]);
      }
      return next;
    });
  };

  // Latest poll decided-voter shares
  const latestDecided = useMemo(() => {
    const d = { ...latestPoll.candidates };
    delete d["Undecided"];
    delete d["Other"];
    const total = Object.values(d).reduce((s, v) => s + v, 0);
    return ALL_DECIDED.map((c) => ({
      name: c,
      pct: +(((d[c] || 0) / total) * 100).toFixed(1),
      color: COLORS[c],
    })).sort((a, b) => b.pct - a.pct);
  }, []);

  const progCoalitionPct = latestDecided
    .filter((d) => PROGRESSIVE_CANDIDATES.includes(d.name))
    .reduce((s, d) => s + d.pct, 0);
  const bissPct = latestDecided.find((d) => d.name === "Daniel Biss")?.pct || 0;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(175deg, #FAFAF8 0%, #F3F1ED 100%)",
        fontFamily: "'Source Sans 3', sans-serif",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=Source+Sans+3:wght@300;400;600;700&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      {/* ─── HERO ───────────────────────────────────────────────────────── */}
      <div
        style={{
          background:
            "linear-gradient(135deg, #1a1a1a 0%, #2d1f1f 50%, #1a1a1a 100%)",
          padding: "52px 40px 44px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 400,
            height: 400,
            background:
              "radial-gradient(circle, rgba(232,69,60,0.12) 0%, transparent 70%)",
            borderRadius: "50%",
            transform: "translate(100px, -100px)",
          }}
        />
        <div
          style={{
            maxWidth: 960,
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              letterSpacing: 3,
              color: "#E8453C",
              textTransform: "uppercase",
              marginBottom: 12,
              fontWeight: 600,
            }}
          >
            IL-09 Democratic Primary · March 17, 2026
          </div>
          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 42,
              fontWeight: 900,
              color: "white",
              margin: "0 0 10px 0",
              lineHeight: 1.15,
              maxWidth: 700,
            }}
          >
            The Spoiler Problem:
            <br />
            <span style={{ color: "#E8453C" }}>
              How a Fragmented Field Hands Biss the Win
            </span>
          </h1>
          <p
            style={{
              fontSize: 16,
              color: "rgba(255,255,255,0.65)",
              maxWidth: 600,
              lineHeight: 1.6,
              margin: "0 0 28px 0",
            }}
          >
            Progressive candidates collectively out-poll Daniel Biss — but only
            if they consolidate. This analysis makes the case for strategic
            coordination using polling data, prediction markets, and coalition
            modeling.
          </p>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <StatCard
              value={`${animatedPct}%`}
              label="Progressive bloc"
              sublabel="Combined decided-voter share"
              color="#E8453C"
              large
            />
            <StatCard
              value={`${bissPct.toFixed(0)}%`}
              label="Daniel Biss"
              sublabel="Current frontrunner"
              color="#4A6FA5"
              large
            />
            <StatCard
              value="7"
              label="Candidates"
              sublabel="Splitting the anti-Biss vote"
              color="#C47F17"
              large
            />
            <StatCard
              value={untilPrimaryLabel}
              label="Until primary"
              sublabel="March 17, 2026"
              color="#888"
              large
            />
          </div>
        </div>
      </div>

      {/* ─── BODY ───────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 24px 60px" }}>
        {/* Section 1: Current Standings */}
        <SectionHeader
          number="01"
          title="The Current Standings Are Deceiving"
          subtitle="In the latest Public Policy Polling survey (Mar 9–10), Biss leads — but the progressive vote is shattered across four candidates."
        />

        <div
          style={{
            background: "white",
            borderRadius: 12,
            padding: 28,
            border: "1px solid #e8e8e8",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "#999",
              fontFamily: "'IBM Plex Mono', monospace",
              marginBottom: 16,
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Latest Poll · Decided Voters · Feb 2026
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {latestDecided.map((d) => (
              <div
                key={d.name}
                style={{ display: "flex", alignItems: "center", gap: 12 }}
              >
                <div
                  style={{
                    width: 140,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#333",
                    textAlign: "right",
                    flexShrink: 0,
                    fontStyle: PROGRESSIVE_CANDIDATES.includes(d.name)
                      ? "normal"
                      : "normal",
                  }}
                >
                  {PROGRESSIVE_CANDIDATES.includes(d.name) && (
                    <span
                      style={{ color: "#E8453C", fontSize: 10, marginRight: 4 }}
                    >
                      ●
                    </span>
                  )}
                  {d.name}
                </div>
                <div
                  style={{
                    flex: 1,
                    height: 28,
                    background: "#f5f5f3",
                    borderRadius: 6,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${(d.pct / 40) * 100}%`,
                      background: PROGRESSIVE_CANDIDATES.includes(d.name)
                        ? `linear-gradient(90deg, ${d.color}, ${d.color}dd)`
                        : d.color + "88",
                      borderRadius: 6,
                      transition: "width 0.8s cubic-bezier(0.22, 1, 0.36, 1)",
                    }}
                  />
                </div>
                <div
                  style={{
                    width: 52,
                    fontSize: 15,
                    fontWeight: 700,
                    color: d.color,
                    fontFamily: "'IBM Plex Mono', monospace",
                    textAlign: "right",
                  }}
                >
                  {d.pct.toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: 18,
              paddingTop: 14,
              borderTop: "2px dashed #E8453C33",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#E8453C", fontSize: 10 }}>●</span>
              <span style={{ fontSize: 13, color: "#666" }}>
                = Progressive-aligned candidate
              </span>
            </div>
            <div
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 15,
                fontWeight: 700,
                color: "#E8453C",
              }}
            >
              Combined: {progCoalitionPct.toFixed(1)}% &gt; Biss at{" "}
              {bissPct.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Section 2: The Bloc Comparison */}
        <SectionHeader
          number="02"
          title="The Progressive Bloc Beats Biss in Every Poll"
          subtitle="When you sum the progressive-aligned candidates (Kat, Amiwala, Simmons, Huynh), they outperform Biss consistently. Grade-weighted average shown below uses FiveThirtyEight-style source quality weights."
        />

        <div
          style={{
            background: "white",
            borderRadius: 12,
            padding: "24px 20px",
            border: "1px solid #e8e8e8",
          }}
        >
          <ResponsiveContainer width="100%" height={340}>
            <BarChart
              data={blocData}
              margin={{ left: 10, right: 10, top: 10, bottom: 10 }}
              barGap={3}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#888" }} />
              <YAxis
                tick={{ fontSize: 12, fill: "#888" }}
                tickFormatter={(v) => v + "%"}
                domain={[0, 50]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 13 }} />
              <Bar
                dataKey="Progressive Bloc"
                fill="#E8453C"
                radius={[4, 4, 0, 0]}
              />
              <Bar dataKey="Biss" fill="#4A6FA5" radius={[4, 4, 0, 0]} />
              <Bar
                dataKey="Fine + Andrew"
                fill="#7B9E6B"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 10,
            }}
          >
            {weightedBlocSummary.map((row) => (
              <div
                key={row.name}
                style={{
                  background: "#fafafa",
                  border: "1px solid #eee",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 12,
                  color: "#666",
                }}
              >
                <span style={{ fontWeight: 700, color: row.color }}>
                  {row.name}: {row.value.toFixed(1)}%
                </span>
                <span> (grade-weighted avg)</span>
              </div>
            ))}
          </div>
        </div>

        {/* Section 3: Polling Trend */}
        <SectionHeader
          number="03"
          title="Kat's Trajectory Is the Strongest Among Progressives"
          subtitle="Among the fragmented progressive candidates, Kat Abughazaleh has the highest and most consistent polling — she's the natural consolidation candidate. Dashed overlays show running grade-weighted averages."
        />

        <div
          style={{
            background: "white",
            borderRadius: 12,
            padding: "24px 20px",
            border: "1px solid #e8e8e8",
          }}
        >
          <ResponsiveContainer width="100%" height={360}>
            <LineChart
              data={trendData}
              margin={{ left: 10, right: 20, top: 10, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#888" }} />
              <YAxis
                tick={{ fontSize: 12, fill: "#888" }}
                tickFormatter={(v) => v + "%"}
                domain={[0, 45]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {ALL_DECIDED.map((c) => (
                <Line
                  key={c}
                  type="monotone"
                  dataKey={c}
                  stroke={COLORS[c]}
                  strokeWidth={0}
                  dot={{
                    r: c === "Kat Abughazaleh" || c === "Daniel Biss" ? 6 : 4,
                    fill: COLORS[c],
                    strokeWidth: 0,
                  }}
                  activeDot={{
                    r: c === "Kat Abughazaleh" || c === "Daniel Biss" ? 8 : 6,
                  }}
                  opacity={
                    c === "Kat Abughazaleh" || c === "Daniel Biss" ? 1 : 0.65
                  }
                />
              ))}
              <Line
                type="monotone"
                dataKey="Kat Abughazaleh (Grade-Weighted Avg)"
                name="Kat (Grade-Weighted Avg)"
                stroke="#E8453C"
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="Daniel Biss (Grade-Weighted Avg)"
                name="Biss (Grade-Weighted Avg)"
                stroke="#4A6FA5"
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="Progressive Coalition (Grade-Weighted Avg)"
                stroke="#B3201B"
                strokeWidth={2}
                strokeDasharray="2 3"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Section 4: Prediction Markets */}
        <SectionHeader
          number="04"
          title="Prediction Markets: Biss's Dominance Is Fragile"
          subtitle="Kalshi data shows Biss's win probability has been declining while Kat's has been climbing — but the split field props him up."
        />

        <div
          style={{
            background: "white",
            borderRadius: 12,
            padding: "24px 20px",
            border: "1px solid #e8e8e8",
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginBottom: 12 }}>
            {["1M", "6M", "ALL"].map((w) => (
              <button
                key={w}
                onClick={() => setKalshiWindow(w)}
                style={{
                  padding: "4px 12px",
                  borderRadius: 6,
                  border: "1px solid",
                  borderColor: kalshiWindow === w ? "#4A6FA5" : "#ddd",
                  background: kalshiWindow === w ? "#4A6FA5" : "white",
                  color: kalshiWindow === w ? "white" : "#888",
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {w}
              </button>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={340}>
            <AreaChart
              data={filteredKalshiData}
              margin={{ left: 10, right: 20, top: 10, bottom: 10 }}
            >
              <defs>
                <linearGradient id="gradBiss" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4A6FA5" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#4A6FA5" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gradKat" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E8453C" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#E8453C" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: "#888" }}
                interval={kalshiWindow === "1M" ? 4 : kalshiWindow === "6M" ? 14 : 29}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#888" }}
                tickFormatter={(v) => v + "%"}
                domain={[0, 90]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 13 }} />
              <Area
                type="stepAfter"
                dataKey="Daniel Biss"
                stroke="#4A6FA5"
                fill="url(#gradBiss)"
                strokeWidth={2.5}
                connectNulls
                dot={false}
              />
              <Area
                type="stepAfter"
                dataKey="Kat Abughazaleh"
                stroke="#E8453C"
                fill="url(#gradKat)"
                strokeWidth={2.5}
                connectNulls
                dot={false}
              />
              <Area
                type="stepAfter"
                dataKey="Laura Fine"
                stroke="#7B9E6B"
                fill="transparent"
                strokeWidth={1.5}
                strokeDasharray="5 3"
                connectNulls
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
          <div
            style={{
              marginTop: 8,
              fontSize: 12,
              color: "#999",
              fontFamily: "'IBM Plex Mono', monospace",
              textAlign: "center",
            }}
          >
            Source: Kalshi prediction market (KXIL9D-26) · Implied win
            probabilities
          </div>
        </div>

        {/* Section 5: Interactive Scenario Builder */}
        <SectionHeader
          number="05"
          title="Build a Consolidation Scenario"
          subtitle="Toggle candidates out of the race and see how their vote share redistributes. The math speaks for itself."
        />

        <div
          style={{
            background: "white",
            borderRadius: 12,
            padding: 28,
            border: "2px solid #E8453C33",
            boxShadow: "0 4px 20px rgba(232,69,60,0.06)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 32,
              flexWrap: "wrap",
              marginBottom: 24,
            }}
          >
            <div style={{ flex: 1, minWidth: 280 }}>
              <div
                style={{
                  fontSize: 12,
                  color: "#999",
                  fontFamily: "'IBM Plex Mono', monospace",
                  marginBottom: 10,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Who drops out? (click to toggle)
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {ALL_DECIDED.filter((c) => c !== "Daniel Biss").map((c) => {
                  const isOut = scenarioDropouts.has(c);
                  return (
                    <button
                      key={c}
                      onClick={() => toggleDropout(c)}
                      style={{
                        padding: "8px 14px",
                        borderRadius: 8,
                        border: "2px solid",
                        borderColor: isOut ? "#E8453C" : "#ddd",
                        background: isOut ? "#E8453C0F" : "white",
                        color: isOut ? "#E8453C" : "#666",
                        fontFamily: "'Source Sans 3', sans-serif",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        textDecoration: isOut ? "line-through" : "none",
                      }}
                    >
                      {c} {isOut ? "✕" : ""}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ minWidth: 220 }}>
              <div
                style={{
                  fontSize: 12,
                  color: "#999",
                  fontFamily: "'IBM Plex Mono', monospace",
                  marginBottom: 10,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Votes go to:
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {ALL_DECIDED.filter((c) => !scenarioDropouts.has(c)).map(
                  (c) => (
                    <button
                      key={c}
                      onClick={() => setRedistributionTarget(c)}
                      style={{
                        padding: "7px 12px",
                        borderRadius: 8,
                        border: "2px solid",
                        borderColor:
                          redistributionTarget === c ? COLORS[c] : "#ddd",
                        background:
                          redistributionTarget === c
                            ? COLORS[c] + "15"
                            : "white",
                        color: redistributionTarget === c ? COLORS[c] : "#888",
                        fontFamily: "'Source Sans 3', sans-serif",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      {c.split(" ").pop()}
                    </button>
                  ),
                )}
              </div>
            </div>
          </div>

          {/* Scenario results */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {scenarioData.map((d) => (
              <div
                key={d.name}
                style={{ display: "flex", alignItems: "center", gap: 12 }}
              >
                <div
                  style={{
                    width: 130,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#333",
                    textAlign: "right",
                    flexShrink: 0,
                  }}
                >
                  {d.name}
                  {d.isTarget && (
                    <span
                      style={{ color: "#E8453C", fontSize: 10, marginLeft: 4 }}
                    >
                      ★
                    </span>
                  )}
                </div>
                <div
                  style={{
                    flex: 1,
                    height: 32,
                    background: "#f5f5f3",
                    borderRadius: 6,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  {/* Original ghost bar */}
                  <div
                    style={{
                      position: "absolute",
                      height: "100%",
                      width: `${(d.original / 55) * 100}%`,
                      background: d.color + "20",
                      borderRadius: 6,
                      borderRight: `2px dashed ${d.color}44`,
                    }}
                  />
                  {/* New bar */}
                  <div
                    style={{
                      position: "relative",
                      height: "100%",
                      width: `${(d.scenario / 55) * 100}%`,
                      background: d.isTarget
                        ? `linear-gradient(90deg, ${d.color}cc, ${d.color})`
                        : d.color + "77",
                      borderRadius: 6,
                      transition: "width 0.6s cubic-bezier(0.22, 1, 0.36, 1)",
                    }}
                  />
                </div>
                <div
                  style={{
                    width: 95,
                    display: "flex",
                    gap: 6,
                    alignItems: "baseline",
                  }}
                >
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: d.color,
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}
                  >
                    {d.scenario.toFixed(1)}%
                  </span>
                  {d.scenario !== d.original && (
                    <span
                      style={{
                        fontSize: 11,
                        color: d.scenario > d.original ? "#2ecc71" : "#999",
                        fontWeight: 600,
                      }}
                    >
                      {d.scenario > d.original ? "+" : ""}
                      {(d.scenario - d.original).toFixed(1)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {scenarioData[0]?.isTarget &&
            scenarioData[0]?.scenario > (scenarioData[1]?.scenario || 0) && (
              <div
                style={{
                  marginTop: 20,
                  padding: "14px 18px",
                  borderRadius: 8,
                  background: "linear-gradient(90deg, #E8453C0A, #E8453C15)",
                  border: "1px solid #E8453C33",
                }}
              >
                <span
                  style={{ fontWeight: 700, color: "#E8453C", fontSize: 14 }}
                >
                  ✦ {scenarioData[0].name} wins this scenario
                </span>
                <span style={{ color: "#666", fontSize: 13, marginLeft: 8 }}>
                  with a{" "}
                  {(
                    scenarioData[0].scenario - (scenarioData[1]?.scenario || 0)
                  ).toFixed(1)}{" "}
                  point lead over {scenarioData[1]?.name}
                </span>
              </div>
            )}
        </div>

        {/* Section 6: The Case */}
        <SectionHeader
          number="06"
          title="The Case for Consolidation"
          subtitle=""
        />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          {[
            {
              icon: "📊",
              title: "The Math Is Clear",
              body: `Progressive candidates (Kat, Amiwala, Simmons, Huynh) combine for ~${progCoalitionPct.toFixed(0)}% of decided voters — more than Biss's ${bissPct.toFixed(0)}%. But fragmentation turns a majority into a loss.`,
            },
            {
              icon: "📈",
              title: "Kat Has the Momentum",
              body: "Kat has polled as high as 18% consistently — the highest of any progressive. Her Kalshi odds peaked at 63% and she maintains the strongest trajectory in the field.",
            },
            {
              icon: "⏰",
              title: "The Clock Is Ticking",
              body: `${untilPrimaryBodyText}, every day without consolidation is another day Biss's plurality holds. Early voting has already begun.`,
            },
            {
              icon: "🎯",
              title: "Precedent Is Clear",
              body: "Spoiler effects have decided primaries for decades. Progressives lose when they split — consolidation behind the strongest candidate is the only viable counter-strategy.",
            },
          ].map((card, i) => (
            <div
              key={i}
              style={{
                background: "white",
                borderRadius: 12,
                padding: "22px 24px",
                border: "1px solid #e8e8e8",
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 10 }}>{card.icon}</div>
              <div
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 17,
                  fontWeight: 700,
                  color: "#1a1a1a",
                  marginBottom: 8,
                }}
              >
                {card.title}
              </div>
              <div style={{ fontSize: 14, color: "#555", lineHeight: 1.6 }}>
                {card.body}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: 48,
            paddingTop: 24,
            borderTop: "1px solid #ddd",
            textAlign: "center",
            color: "#999",
            fontSize: 12,
            fontFamily: "'IBM Plex Mono', monospace",
          }}
        >
          Data: Impact Research, GBAO Strategies, Data for Progress, MDW
          Communications, Kalshi prediction markets
          <br />
          Bayesian model: Dirichlet-Multinomial GP (Matérn 5/2 HSGP) · 6 polls ·
          n=500–917
          <br />
          Analysis as of Mar 15, 2026. This is a persuasive analytical tool, not
          a neutral forecast.
        </div>
      </div>
    </div>
  );
}

const root = ReactDOM.createRoot(document.querySelector("#root"));
root.render(<IL09Dashboard />);
